import { memo, useState } from 'react';

import { ZipFileEntry, formatBytes } from '@web/app/shared/utils/zipParser';
import { ChevronDown, ChevronRight, File, Folder, FolderOpen } from 'lucide-react';

interface FileTreeViewProps {
    entries: ZipFileEntry[];
    totalFiles: number;
    totalSize: number;
    onFileClick?: (filePath: string) => void;
}

interface TreeNodeProps {
    entry: ZipFileEntry;
    level: number;
    onFileClick?: (filePath: string) => void;
}

const TreeNode = memo(
    ({ entry, level, onFileClick }: TreeNodeProps) => {
        const [isExpanded, setIsExpanded] = useState(level === 0);
        const hasChildren = entry.children && entry.children.length > 0;

        const handleClick = () => {
            if (entry.isDirectory && hasChildren) {
                setIsExpanded(!isExpanded);
            } else if (!entry.isDirectory && onFileClick) {
                onFileClick(entry.path);
            }
        };

        return (
            <div>
                <div
                    className={`flex items-center gap-2 py-1.5 px-2 hover:bg-gray-50 rounded ${
                        entry.isDirectory || onFileClick ? 'cursor-pointer' : 'cursor-default'
                    }`}
                    style={{ paddingLeft: `${level * 20 + 8}px` }}
                    onClick={handleClick}
                >
                    {/* Expand/Collapse Icon */}
                    {hasChildren ? (
                        <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                            {isExpanded ? (
                                <ChevronDown className="w-4 h-4 text-gray-500" />
                            ) : (
                                <ChevronRight className="w-4 h-4 text-gray-500" />
                            )}
                        </div>
                    ) : (
                        <div className="w-4 h-4 flex-shrink-0" />
                    )}

                    {/* File/Folder Icon */}
                    {entry.isDirectory ? (
                        isExpanded ? (
                            <FolderOpen className="w-4 h-4 text-blue-500 flex-shrink-0" />
                        ) : (
                            <Folder className="w-4 h-4 text-blue-500 flex-shrink-0" />
                        )
                    ) : (
                        <File className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    )}

                    {/* File/Folder Name */}
                    <span className="text-sm text-gray-700 truncate flex-1">{entry.name}</span>

                    {/* File Size */}
                    {!entry.isDirectory && entry.size > 0 && (
                        <span className="text-xs text-gray-500 flex-shrink-0">{formatBytes(entry.size)}</span>
                    )}
                </div>

                {/* Children */}
                {hasChildren && isExpanded && (
                    <div>
                        {entry.children?.map(child => (
                            <TreeNode key={child.path} entry={child} level={level + 1} onFileClick={onFileClick} />
                        ))}
                    </div>
                )}
            </div>
        );
    },
    (prevProps, nextProps) => {
        // Custom comparison for performance
        return prevProps.entry.path === nextProps.entry.path && prevProps.level === nextProps.level;
    }
);

export const FileTreeView = ({ entries, totalFiles, totalSize, onFileClick }: FileTreeViewProps) => {
    return (
        <div className="border rounded-lg overflow-hidden">
            {/* Header */}
            <div className="bg-gray-50 px-4 py-2 border-b">
                <div className="flex items-center justify-between text-sm">
                    <span className="font-medium text-gray-700">
                        {totalFiles} {totalFiles === 1 ? 'file' : 'files'}
                    </span>
                    <span className="text-gray-500">{formatBytes(totalSize)}</span>
                </div>
            </div>

            {/* Tree */}
            <div className="max-h-96 overflow-y-auto p-2">
                {entries.length === 0 ? (
                    <div className="text-center py-8 text-gray-500 text-sm">No files found</div>
                ) : (
                    entries.map(entry => (
                        <TreeNode key={entry.path} entry={entry} level={0} onFileClick={onFileClick} />
                    ))
                )}
            </div>
        </div>
    );
};
